import { useState, useEffect } from 'react';
import { getCurrentUser, signOut as amplifySignOut, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

/**
 * Decode the payload segment of a JWT without verifying the signature.
 * Used only to extract display claims (name, email) from the Cognito ID token
 * when fetchUserAttributes() fails for federated (Google/Apple) users.
 */
function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [userAttributes, setUserAttributes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkUser() {
      try {
        const u = await getCurrentUser();
        if (cancelled) return;
        setUser(u);

        // Fetch email/name attributes for display
        let attrs = null;
        try {
          attrs = await fetchUserAttributes();
        } catch {
          // fetchUserAttributes often fails for federated (Google/Apple) users.
          // Fall back to decoding the ID token claims.
        }

        if (!attrs || (!attrs.name && !attrs.email)) {
          try {
            const session = await fetchAuthSession();
            const idToken = session?.tokens?.idToken?.toString();
            if (idToken) {
              const claims = decodeJwtPayload(idToken);
              if (claims) {
                attrs = {
                  ...(attrs || {}),
                  name: attrs?.name || claims.name || null,
                  email: attrs?.email || claims.email || null,
                };
              }
            }
          } catch {
            // Token decode failed — not critical
          }
        }

        if (!cancelled && attrs) setUserAttributes(attrs);
      } catch {
        if (!cancelled) { setUser(null); setUserAttributes(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    checkUser();

    // Re-check after OAuth redirect completes — signInWithRedirect (Google/Apple)
    // finishes asynchronously; without this listener AuthGuard bounces the user
    // to /login before tokens are stored.
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signIn' || payload.event === 'signInWithRedirect') {
        checkUser();
      }
      if (payload.event === 'signOut') {
        if (!cancelled) { setUser(null); setUserAttributes(null); }
      }
    });

    return () => { cancelled = true; unsubscribe(); };
  }, []);

  async function signOut() {
    await amplifySignOut();
    setUser(null);
    setUserAttributes(null);
  }

  return { user, userAttributes, loading, signOut };
}

export async function getIdToken() {
  const session = await fetchAuthSession();
  return session?.tokens?.idToken?.toString() ?? null;
}
